import { parseRef, RefError, toSlug } from "@bibleunleashed/shared";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { useUser } from "@/lib/auth";
import { useCommentaryIndex } from "@/lib/commentary";

export function CommentaryIndex() {
  const { data: user } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: chapters, isLoading, error } = useCommentaryIndex();
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [refError, setRefError] = useState<string | null>(null);

  const start = (e: FormEvent) => {
    e.preventDefault();
    try {
      navigate(`/commentary/${toSlug(parseRef(query))}`);
    } catch (err) {
      setRefError(err instanceof RefError ? err.message : "Try a chapter like Nehemiah 1");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[720px] px-6 py-16">
        <PageText id="commentary" titleClassName="font-serif text-[38px] font-semibold tracking-[-0.015em]" bodyClassName="mt-3 font-serif text-lg" />

        {isLoading && <p className="mt-10 font-serif text-ink-3">Loading…</p>}
        {error && <p className="mt-10 font-serif text-ink-2">Couldn’t load the list: {error.message}</p>}
        {chapters && chapters.length === 0 && <p className="mt-10 font-serif text-ink-3">Nothing published yet.</p>}

        <ul className="mt-10 border-t border-rule">
          {chapters?.map((c) => (
            <li key={c.osis} className="border-b border-rule">
              <Link href={`/commentary/${toSlug({ book: c.book, chapter: c.chapter })}`} className="flex items-baseline gap-3 py-4 hover:text-accent">
                <span className="w-[130px] shrink-0 font-sans text-[12px] tracking-[0.06em] text-ink-3 uppercase">{c.display}</span>
                <span className="font-serif text-[19px]">{c.title || c.display}</span>
                {c.tags.length > 0 && (
                  <span className="ml-auto hidden shrink-0 font-sans text-[11px] text-ink-3 sm:inline">{c.tags.slice(0, 3).join(" · ")}</span>
                )}
                {c.status === "draft" && (
                  <span className="ml-auto rounded-full border border-rule px-2 py-0.5 font-sans text-[10px] tracking-[0.08em] text-ink-3 uppercase">Draft</span>
                )}
              </Link>
            </li>
          ))}
        </ul>

        {isAdmin && (
          <form onSubmit={start} className="mt-10 flex flex-wrap items-center gap-2">
            <label className="font-sans text-[12px] tracking-[0.06em] text-ink-3 uppercase" htmlFor="new-commentary">
              Write on
            </label>
            <input
              id="new-commentary"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setRefError(null);
              }}
              placeholder="Nehemiah 1"
              className="rounded-md border border-rule bg-paper-2 px-3 py-1.5 text-[14px] text-ink focus:border-accent focus-visible:outline-none"
            />
            <button className="rounded-md bg-accent px-3 py-1.5 text-[13px] font-medium text-white">Open</button>
            {refError && <span className="text-[13px] text-accent">{refError}</span>}
          </form>
        )}
      </main>
    </div>
  );
}
